/**
 * Recently viewed products, kept in the browser (no app needed).
 *
 * - recentlyViewed.track(handle) is called on product pages.
 * - <recently-viewed> renders the history with server-rendered cards
 *   fetched in parallel from /products/<handle>?section_id=card-fragment,
 *   and stays hidden while there is no history.
 */
import { storage } from '@theme/core';

const KEY = 'theme:recently-viewed';
const MAX = 20;

export const recentlyViewed = {
  list() {
    const items = storage.get(KEY, []);
    return Array.isArray(items) ? items : [];
  },
  track(handle) {
    if (!handle) return;
    storage.set(KEY, [handle, ...this.list().filter((h) => h !== handle)].slice(0, MAX));
  },
  clear() {
    storage.set(KEY, []);
  },
};

/** Fetches one server-rendered card; resolves to null for missing products. */
export async function fetchCard(handle) {
  const root = window.Theme?.routes?.root || '/';
  const base = root.endsWith('/') ? root : `${root}/`;
  try {
    const response = await fetch(`${base}products/${encodeURIComponent(handle)}?section_id=card-fragment`);
    if (!response.ok) return null;
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    return doc.querySelector('[data-card-fragment]');
  } catch {
    return null;
  }
}

class RecentlyViewed extends HTMLElement {
  async connectedCallback() {
    if (window.Shopify?.designMode) return;
    this.list = this.querySelector('[data-recent-list]');
    this.querySelector('[data-recent-clear]')?.addEventListener('click', () => {
      recentlyViewed.clear();
      this.hidden = true;
    });

    const exclude = this.dataset.exclude;
    const handles = recentlyViewed
      .list()
      .filter((h) => h !== exclude)
      .slice(0, Number(this.dataset.limit) || 8);
    if (!handles.length) return;

    const cards = (await Promise.all(handles.map(fetchCard))).filter(Boolean);
    if (!cards.length) return;

    this.list.replaceChildren(
      ...cards.map((card) => {
        const li = document.createElement('li');
        li.setAttribute('data-carousel-slide', '');
        li.append(...card.childNodes);
        return li;
      }),
    );
    this.hidden = false;
    // Let the carousel recalculate now that it has slides
    this.querySelector('carousel-slider')?.dispatchEvent(new Event('scroll'));
    this.list.dispatchEvent(new Event('scroll'));
  }
}

if (!customElements.get('recently-viewed')) customElements.define('recently-viewed', RecentlyViewed);

// Product pages register themselves
const current = document.querySelector('[data-recently-viewed-handle]');
if (current) recentlyViewed.track(current.dataset.recentlyViewedHandle);
